import { useEffect } from 'react';

import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useUserContext } from '../Contexts/User';
import { useGetCase } from '@/api/queries/useGetCase';

/**
 * Redirects from old frontend routes to their new frontend equivalents.
 * This function should be called in components that need to redirect to the new frontend.
 *
 * Route mappings based on the routing analysis:
 * - /app/chronos/case-editor/summary?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>
 * - /app/chronos/case-editor/data/facts?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/facts
 * - /app/chronos/case-editor/data/chronology?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/chronologies
 * - /app/chronos/case-editor/data/documents?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/docs
 * - /app/chronos/case-editor/data/persons?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/entities
 * - /app/chronos/case-editor/data/inconsistencies?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/inconsistencies
 * - /app/chronos/case-editor/data/inconsistency-report?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/reports
 * - /app/chronos/case-editor/assistant?caseId=<ID> -> /matters/<MATTER_ID>/subsets/<ID>/kim
 * - /app/chronos/case-editor/assistant?caseId=<ID>&threadId=<THREAD_ID> -> /matters/<MATTER_ID>/subsets/<ID>/kim/<THREAD_ID>
 * - /app/chronos/case-editor/settings/metadata?caseId=<ID> -> /matters/<MATTER_ID>/settings/metadata
 * - /app/chronos/case-editor/settings/details?caseId=<ID> -> /matters/<MATTER_ID>/settings/issues
 * - /app/chronos/case-editor/settings/documents?caseId=<ID> -> /matters/<MATTER_ID>/docs/upload
 * - /app/chronos/case-editor/settings/runs?caseId=<ID> -> /matters/<MATTER_ID>/settings/run-history
 * - /app/chronos/case-editor/settings/uploads?caseId=<ID> -> /matters/<MATTER_ID>/settings/upload-history
 * - /app/chronos/explore?matterId=<ID> -> /matters/<ID>
 * - /app/chronos/matters -> /matters
 * - /app/chronos/organisations -> /admin/organisations
 * - /app/chronos/organisation/:orgId -> /admin/organisations/:organisationId
 * - /app/chronos/organisation/:orgId/users -> /admin/organisations/:organisationId/users
 * - /app/chronos/organisation/:orgId/runs -> /admin/organisations/:organisationId/runs
 * - /app/chronos/usage-dashboard -> /usage
 * - /app/chronos/users -> /manage-users
 * - /app/chronos/guide -> /guide
 * - /app/chronos/client-materials -> /client-materials
 * - /app/chronos/configuration/credits -> /organisation-settings/credits
 * - /app/chronos/configuration/integrations -> /organisation-settings/integrations
 * - /app/chronos/configuration/roles -> /organisation-settings/roles
 * - /app/release-notes -> /release-notes
 */
export function RedirectToNewFrontend() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { user } = useUserContext();

  // Get caseId from query params (used in most old routes)
  const caseId = searchParams.get('caseId');

  // Get matterId from path params (used in new routes that might be partially migrated)
  const matterId = params.matterId;

  // Fetch case data to get matterId
  const { data: caseData } = useGetCase(caseId);

  useEffect(() => {
    const currentPath = location.pathname;

    // Case Editor Routes
    if (currentPath.startsWith('/app/chronos/case-editor')) {
      if (!caseId) {
        console.warn('No caseId found in query params for redirect');
        return;
      }

      // Get matterId from path params or fetched case data
      const resolvedMatterId = matterId || caseData?.matterId;

      // Wait for case data if matterId is not available yet
      if (!resolvedMatterId) {
        return;
      }

      // Summary page
      if (currentPath.includes('/case-editor/summary')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}`);
        return;
      }

      // Facts page
      if (currentPath.includes('/case-editor/data/facts')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/facts`);
        return;
      }

      // Chronology page
      if (currentPath.includes('/case-editor/data/chronology')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/chronologies`);
        return;
      }

      // Documents page
      if (currentPath.includes('/case-editor/data/documents')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/docs`);
        return;
      }

      // Persons/Entities page
      if (currentPath.includes('/case-editor/data/persons')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/entities`);
        return;
      }

      // Inconsistencies page
      if (currentPath.includes('/case-editor/data/inconsistencies')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/inconsistencies`);
        return;
      }

      // Inconsistency Report page
      if (currentPath.includes('/case-editor/data/inconsistency-report')) {
        navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/reports`);
        return;
      }

      // Assistant/Kim page
      if (currentPath.includes('/case-editor/assistant')) {
        const threadId = searchParams.get('threadId');
        if (threadId) {
          navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/kim/${threadId}`);
        } else {
          navigate(`/matters/${resolvedMatterId}/subsets/${caseId}/kim`);
        }
        return;
      }

      // Settings pages
      if (currentPath.includes('/case-editor/settings/metadata')) {
        navigate(`/matters/${resolvedMatterId}/settings/metadata`);
        return;
      }

      if (currentPath.includes('/case-editor/settings/details')) {
        navigate(`/matters/${resolvedMatterId}/settings/issues`);
        return;
      }

      if (currentPath.includes('/case-editor/settings/documents')) {
        navigate(`/matters/${resolvedMatterId}/docs/upload`);
        return;
      }

      if (currentPath.includes('/case-editor/settings/runs')) {
        navigate(`/matters/${resolvedMatterId}/settings/run-history`);
        return;
      }

      if (currentPath.includes('/case-editor/settings/uploads')) {
        navigate(`/matters/${resolvedMatterId}/settings/file-history`);
        return;
      }
    }

    // Explore page with matterId
    if (currentPath === '/app/chronos/explore') {
      const matterIdFromQuery = searchParams.get('matterId');
      if (matterIdFromQuery) {
        navigate(`/matters/${matterIdFromQuery}`);
        return;
      }
    }

    // Simple route redirects (no dynamic params needed)
    const simpleRedirects: Record<string, string> = {
      '/app/chronos/matters': '/matters',
      '/app/chronos/organisations': '/admin/organisations',
      '/app/chronos/usage-dashboard': '/usage',
      '/app/chronos/users': '/manage-users',
      '/app/chronos/guide': '/guide',
      '/app/chronos/client-materials': '/client-materials',
      '/app/chronos/configuration/credits': '/organisation-settings/credits',
      '/app/chronos/configuration/integrations': '/organisation-settings/integrations',
      '/app/chronos/configuration/roles': '/organisation-settings/roles',
      '/app/release-notes': '/release-notes',
    };

    if (simpleRedirects[currentPath]) {
      navigate(simpleRedirects[currentPath]);
      return;
    }

    // Organisation routes with params
    const orgMatch = currentPath.match(/^\/app\/chronos\/organisation\/([^/]+)(\/.*)?$/);
    if (orgMatch) {
      const orgId = orgMatch[1];
      const subPath = orgMatch[2] || '';

      if (subPath === '/users') {
        navigate(`/admin/organisations/${orgId}/users`);
        return;
      }

      if (subPath === '/runs') {
        navigate(`/admin/organisations/${orgId}/runs`);
        return;
      }

      // Default organisation page
      navigate(`/admin/organisations/${orgId}`);
      return;
    }

    // No redirect pattern matched — fall back to matters
    navigate('/matters', { replace: true });
  }, [location.pathname, searchParams, navigate, matterId, caseData, caseId, user?.organisationId]);
  return null;
}
